import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";

import { useAuthenticate } from "../cacheQueries";
import type { Capability } from "../APIClient";

/**
 * Route guard: render the page only if the session holds `capability`.
 *
 * WHY THIS EXISTS SEPARATELY FROM THE NAV GATING
 *   Hiding a nav link hides a link. The routes in main.tsx were guarded on
 *   authentication alone, so before this, typing /configusers loaded the Users
 *   page for anybody with a session — the member list rendered (it comes from
 *   companydetails, which any member may read) and only the write actions
 *   failed. With one privileged role that was untidy; with operators it would
 *   be wrong, because "an operator cannot enter the Users page" is the entire
 *   definition of the role.
 *
 *   The server enforces every one of these capabilities independently. This
 *   guard exists so the product behaves the way it is described, not as the
 *   security boundary.
 *
 * THE THREE-WAY DECISION, and why it is not two-way
 *   "Allowed" and "denied" are not the only states. There is also "we do not
 *   know yet", and collapsing it into either of the others is a visible bug:
 *   treating unknown as allowed flashes the page to someone who may not be
 *   permitted, and treating unknown as denied bounces a legitimate admin to the
 *   dashboard on every cold load. So unknown renders nothing for a tick.
 *
 *   Reading the capability list off /authenticate rather than a query of its own
 *   is what keeps that simple: there is exactly one upstream request to be
 *   pending, so "do we know yet" has one answer instead of three that can
 *   disagree.
 *
 * ON ERROR it shows the denial rather than redirecting, so a transient API
 * failure does not silently teleport someone away from the URL they asked for —
 * React Query keeps retrying underneath and the page appears when it succeeds.
 */
export function RequireCapability({
  capability,
  children,
  /**
   * Where to send a caller who is definitively not allowed. A redirect (rather
   * than an inline message) is right for a page they reached by typing a URL
   * they were never shown a link to.
   */
  redirectTo = "/",
}: {
  capability: Capability;
  children: React.ReactNode;
  redirectTo?: string;
}) {
  const { data, isPending, isError } = useAuthenticate();
  const { t } = useTranslation();

  // Still establishing the session.
  if (isPending) return null;

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-12 text-center font-primary">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        <p className="m-0 text-sm text-muted-foreground">
          {t(
            "permissions.checkFailed",
            "Could not check your permissions. Retrying…",
          )}
        </p>
      </div>
    );
  }

  // fetchAuthenticate resolves to null on 401 rather than throwing, so a missing
  // session lands here, not in isError. main.tsx's own effect redirects to
  // /login; rendering nothing avoids racing it with a second navigation.
  if (!data) return null;

  const capabilities = (data as { capabilities?: Capability[] }).capabilities;
  if (!capabilities?.includes(capability)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export default RequireCapability;
